import {
  getAddressGeocodingAccess,
  getBillingCatalogsAccess,
  getErpIntegration,
  getMiraModeAccess,
  getShowDeviceManagementInCockpitAccess,
  isIresClient
} from "../data/repository/ClientRepository";
import { UserCapability } from "@/data/models/UserCapability";
import { doesUserHaveCapability } from "../data/models/RoleSettings";
import User, { isAdmin, isIresAdminAccount, Role } from "../data/models/User";
import {
  configureAdministrationCapabilitiesClientAccess,
  configureAdminsitrationSettingsClientAccess,
  defineOwnReportSettingsClientAccess,
  defineOwnSystemConfigurationClientAccess,
  defineReportSettingsForGroupClientAccess,
  deviceAdministrationAccess,
  isDevicesOnlyWebsite,
  isOfficeAdminPortalClient,
  manageAllDevicesClientAccess,
  allDevicesSettingsClientAccess,
  manageGeoEncodingClientAccess,
  manageHyDryDevicesClientAccess,
  manageHyDryMessagesSettingsClientAccess,
  manageMessagesClientAccess,
  manageProjectsClientAccess,
  manageProjectManagementSettingsClientAccess,
  manageRolesClientAccess,
  manageSchedulingClientAccess,
  manageTimeTrackingClientAccess,
  manageUsersClientAccess,
  manageVehiclesClientAccess,
  clientAdministrationAccess,
  manageDeviceCheckAccess,
  manageBranchesClientAccess,
  viewExternalSystemsClientAccess,
} from "./ClientPermissions";
import { Capability } from "@/data/models/Capability";
import { ErpIntegration } from "@/data/models/Client";
import { ProjectSettingsOptions, ProjectSettingsV2 } from "@/data/models/Settings";
import { ClientModule } from "@/store/modules/ClientModule";
import { isViewAsSelected } from "@/store/modules/TargetUserModule";

const ADMIN_CAPABILITIES = [
  /* * * * * * * * * *  * * * * * * * * */
  /* * * * * * *   COCKPIT  * * * * * * */
  /* * * * * * * * * *  * * * * * * * * */
  Capability.SWITCH_TARGET_USER,
  Capability.SHOW_RELEVANT_PROJECTS_TILE,
  Capability.SHOW_ALL_PROJECTS_TILE,
  /* * * * * * * * * *  * * * * * * * * */

  /* * * * * * * * * *  * * * * * * * * */
  /* * * * * * *  PROJECTS  * * * * * * */
  /* * * * * * * * * *  * * * * * * * * */
  Capability.SPECIAL_RIGHTS_FOR_PROJECT_TYPE_SELECTION,
  Capability.VIEW_ARCHIVED_PROJECTS,
  Capability.RESTORE_PROJECT,
  Capability.REOPEN_COMPLETED_PHASES,
  /* * * * * * * * * *  * * * * * * * * */

  /* * * * * * * * * *  * * * * * * * * */
  /* * * * *  HY-DRY DEVICES  * * * * * */
  /* * * * * * * * * *  * * * * * * * * */
  Capability.SEE_ALL_HY_DRY_DEVICES,
  Capability.CONTROL_COMBOX_VIA_IOT,
  Capability.VIEW_POWER_ON_BUTTON,
  /* * * * * * * * * *  * * * * * * * * */

  /* * * * * * * * * *  * * * * * * * * */
  /* * * * *  USER MANAGEMENT * * * * * */
  /* * * * * * * * * *  * * * * * * * * */
  Capability.ADMIN_RIGHTS,
  /* * * * * * * * * *  * * * * * * * * */

  /* * * * * * * * * *  * * * * * * * * */
  /* * * * *  ADMINISTRATION  * * * * * */
  /* * * * * * * * * *  * * * * * * * * */
  Capability.CONFIGURE_ADMINISTRATION_SETTINGS,
  Capability.CONFIGURE_ADMINISTRATION_CAPABILITIES,
  /* * * * * * * * * *  * * * * * * * * */

];

const IRES_CAPABILITIES = [
  /* * * * * * * * * *  * * * * * * * * */
  /* * * * * * *   COCKPIT  * * * * * * */
  /* * * * * * * * * *  * * * * * * * * */
  Capability.SWITCH_TARGET_USER,
  Capability.SHOW_MY_MESSAGES_TILE,
  Capability.SHOW_MY_PROJECTS_TILE,
  Capability.SHOW_RELEVANT_PROJECTS_TILE,
  Capability.SHOW_ALL_PROJECTS_TILE,
  Capability.SHOW_PROJECT_ORGRANISATION_TILE,
  Capability.SHOW_MY_APPOINTMENTS_TILE,
  Capability.SHOW_APPOINTMENT_MANAGEMENTILE_TILE,
  Capability.SHOW_MY_HY_DRY_DEVICES_TILE,
  Capability.SHOW_ALL_DEVICES_TILE,
  /* * * * * * * * * *  * * * * * * * * */

  /* * * * * * * * * *  * * * * * * * * */
  /* * * * * * *  PROJECTS  * * * * * * */
  /* * * * * * * * * *  * * * * * * * * */
  Capability.SEE_ALL_PROJECTS,
  Capability.CREATE_PROJECT,
  Capability.EDIT_PROJECT,
  Capability.CREATE_APPOINTMENT,
  Capability.REOPEN_COMPLETED_PHASES,
  Capability.DELETE_PROJECT,
  Capability.LEAK_DETECTION_PROJECTS,
  Capability.DRYING_PROJECTS,
  Capability.OFFICE_PROJECTS,
  Capability.PROJECT_MANAGEMENT,
  Capability.PROJECT_SUPERVISION,
  Capability.DAMAGE_ASSESSMENT_AND_LEAK_DETECTION_PROJECTS,
  Capability.DRYING_AND_REPAIR_PROJECTS,
  Capability.MANAGER_PROJECTS,
  Capability.VIEW_ARCHIVED_PROJECTS,
  Capability.RESTORE_PROJECT,
  Capability.MOVE_PROJECT,
  Capability.DEFINE_PROJECT_TYPES,

  Capability.SPECIAL_RIGHTS_FOR_PROJECT_TYPE_SELECTION,
  Capability.ALWAYS_ALLOWED_TO_CONCLUDE_PROJECT,
  Capability.ALWAYS_ALLOWED_TO_EDIT_PROJECT,
  Capability.ALWAYS_ALLOWED_TO_VIEW_TIME_TRACKING,
  Capability.ALWAYS_ALLOWED_TO_EDIT_DEVICE_ASSIGNMENTS,
  Capability.ALWAYS_ALLOWED_TO_CORRECT_METER_READINGS,
  Capability.ALWAYS_ALLOWED_TO_ASSIGN_DEVICES,
  Capability.SKIP_ASSIGNMENT_DATA_VALIDATION,
  Capability.ALLOWED_TO_SEE_PROJECT_APP_VERSION,
  /* * * * * * * * * *  * * * * * * * * */

  /* * * * * * * * * *  * * * * * * * * */
  /* * * * *  HY-DRY DEVICES  * * * * * */
  /* * * * * * * * * *  * * * * * * * * */
  Capability.SEARCH_ANY_HY_DRY_DEVICE_ICON,
  Capability.SEE_ALL_HY_DRY_DEVICES,
  Capability.EDIT_HY_DRY_DEVICES,
  Capability.VIEW_POWER_INFORMATION,
  Capability.VIEW_WEATHER_INFORMATION,
  Capability.VIEW_INTENSITY,
  
  Capability.CONTROL_COMBOX_VIA_IOT,
  Capability.VIEW_POWER_ON_BUTTON,
  Capability.VIEW_TEST_RELAYS_BUTTON,
  Capability.IOT_COMMANDS_ACCESS,
  Capability.SHOW_ALL_INFO,
  Capability.SHOW_OPEN_SETTINGS_ICON,
  Capability.SHOW_ADDITIONAL_DEVICE_INFO,

  Capability.SHOW_PROGRAM_QUALITY_RATING,
  Capability.VIEW_HY_DRY_DEVICE_CONTROL,
  Capability.VIEW_HY_DRY_DEVICE_DETAILS_OF_ALL_CLIENTS,
  Capability.VIEW_RUNS,
  Capability.VIEW_RUN_DETAILS,
  Capability.VIEW_RUN_STARTS_CAUSED_BY_MAX_RUN_DURATION,

  Capability.MARK_END_DATE_MANUAL_CORRECTIONS,
  Capability.REMOVE_OUTLIER_SAMPLES,
  Capability.FORCE_LOAD_RUNS_VIA_IOT,

  Capability.CHANGE_PROGRAM_DURING_DRYING,

  Capability.VIEW_SENSOR_DIAGNOSTICS,

  Capability.VIEW_PROJECT_DETAILS_OF_ANOTHER_CLIENT,

  /* * * *   HY-DRY EVENT LOGS  * * * * */
  Capability.SEE_LID_AND_PAUSE_EVENT_LOGS,
  Capability.SEE_SENSOR_EVENT_LOGS,
  Capability.SEE_ALL_EVENT_LOGS,
  Capability.TOGGLE_STEP_EVENT_LOGS,
  Capability.VIEW_CYCLE_INFO,
  Capability.VIEW_STEP_INFO,
  Capability.VIEW_LIMIT_LINES_FOR_RUNS,

  /* * * * * *  DRYING HISTORY  * * * * */
  Capability.VIEW_DRYING_HISTORY_OF_ALL_CLIENTS,
  /* * * * * * * * * *  * * * * * * * * */

  /* * * * * * * * * *  * * * * * * * * */
  /* * * * * *  ALL DEVICES   * * * * * */
  /* * * * * * * * * *  * * * * * * * * */
  Capability.EDIT_ALL_DEVICES,
  /* * * * * * * * * *  * * * * * * * * */

  /* * * * * * * * * *  * * * * * * * * */
  /* * * * * * *  MESSAGES *  * * * * * */
  /* * * * * * * * * *  * * * * * * * * */
  Capability.SEE_ALL_MESSAGES,
  /* * * * * * * * * *  * * * * * * * * */

  /* * * * * * * * * *  * * * * * * * * */
  /* * * * * * *  SETTINGS  * * * * * * */
  /* * * * * * * * * *  * * * * * * * * */
  Capability.VIEW_USER_SETTINGS,
  Capability.VIEW_COMPANY_SETTINGS,
  Capability.VIEW_REPORT_SETTINGS,
  Capability.VIEW_HY_DRY_DEVICE_SETTINGS,
  Capability.VIEW_ALL_DEVICES_SETTINGS,
  Capability.VIEW_MESSAGE_SETTINGS,
  Capability.VIEW_PROJECT_SETTINGS,
  Capability.VIEW_SYSTEM_SETTINGS_SECTION,
  Capability.VIEW_SYSTEM_SETTINGS,
  Capability.VIEW_TIME_TRACKING_SETTINGS,
  Capability.VIEW_SCHEDULING_SETTINGS,
  Capability.VIEW_IRES_SETTINGS,
  Capability.VIEW_AI_PREDICTIONS,
  Capability.VIEW_USERS_CONFIGURATION_SETTINGS_ITEM,
  Capability.VEHICLE_MANAGEMENT,
  Capability.DEVICE_CHECK_MANAGEMENT,
  Capability.ROLE_MANAGEMENT,
  Capability.VIEW_MAP_PAGE,
  Capability.VIEW_MIRA,
  Capability.VIEW_MIRA_PERCENTILES,
  Capability.VIEW_MIRA_MARKET_CLIENTS,
  Capability.VIEW_MIRA_MARKET_COUNTRIES,
  Capability.VIEW_EDIT_DEVICE_SETTINGS_ITEM,
  Capability.EDIT_IRES_CATEGORIES_AND_TEMPLATES,
  Capability.IMPORT_DEVICES,
  Capability.MESSAGE_SELECTION,
  Capability.VIEW_MAILBOX,
  Capability.VIEW_SYSTEM_CONFIGURATIONS,
  Capability.VIEW_DEVICE_MANAGER_SETTINGS,
  Capability.VIEW_FINISHED_PROJECTS_SETTINGS,

  Capability.CONFIGURE_ADMINISTRATION_SETTINGS,
  Capability.SYSTEM_CONFIGURATION_DISTRIBUTION,
  Capability.CONFIGURE_ADMINSTRATOR_CLIENT,
  /* * * * * * * * * *  * * * * * * * * */

  /* * * * * * * * * *  * * * * * * * * */
  /* * * * *  USER MANAGEMENT * * * * * */
  /* * * * * * * * * *  * * * * * * * * */
  Capability.ADMIN_RIGHTS,
  Capability.CREATE_USER,
  Capability.EDIT_ADMIN,
  Capability.EDIT_OWN_ADMIN,
  Capability.EDIT_USER,
  Capability.EDIT_VALIDATION,
  Capability.BASIC_LICENCE,
  Capability.EDIT_SHARED_EMAIL_ADDRESS,
  Capability.SKIP_SETTING_SESSION_TOKEN,
  Capability.GRANT_ALL_LICENCES,
  Capability.DEFINE_SHARED_USER_ACCESS,
  Capability.DEFINE_SHARED_USER_MASTER_CLIENT_ACCESS,
  Capability.SELECT_DEV_LANGUAGES,
  Capability.SKIP_USER_DATA_VALIDATION,
  /* * * * * * * * * *  * * * * * * * * */

  /* * * * * * * * * *  * * * * * * * * */
  /* * * * *  ADMINISTRATION  * * * * * */
  /* * * * * * * * * *  * * * * * * * * */
  Capability.DEFINE_SYSTEM_CONFIGURATION,
  Capability.CREATE_MEMBER_CLIENTS,
  Capability.MANAGE_MEMBER_CLIENTS,
  Capability.VIEW_EXTERNAL_SYSTEMS,
  /* * * * * * * * * *  * * * * * * * * */

  /* * * * * * * * * *  * * * * * * * * */
  /* * * *  DATA VIEWER / LV  * * * * * */
  /* * * * * * * * * *  * * * * * * * * */
  Capability.EDIT_DATA_VIEWER,
  Capability.VIEW_BILLING_CATALOGS,
  /* * * * * * * * * *  * * * * * * * * */

  /* * * * * * * * * *  * * * * * * * * */
  /* * * * *  UNRELEASED / WIP  * * * * */
  /* * * * * * * * * *  * * * * * * * * */
  // Features complete in `development` but held back from the client release. Move OUT of this
  // array to release. See docs/operations/unreleased-features.md.
  Capability.TOGGLE_DARK_MODE,
  /* * * * * * * * * *  * * * * * * * * */

  /* * * * * * * * * *  * * * * * * * * */
  /* * * * * *  ADMIN PORTAL  * * * * * */
  /* * * * * * * * * *  * * * * * * * * */
  // Nothing of the admin portal is IRES-wide. The capabilities themselves (Capability.RATE_DRYINGS,
  // Capability.MANAGE_PRODUCTION, Capability.MANAGE_CLIENTS) must NOT be listed here, or every
  // user of the IRES-typed admin client would receive them without their role ever being
  // consulted; and the right to configure them is a named SysAdmin's, see isNamedPortalSysAdmin().
  /* * * * * * * * * *  * * * * * * * * */
];

// Capabilities that does not require any permissions
const GENERAL_CAPABILITIES = [
  /* * * * * * * * * *  * * * * * * * * */
  /* * * * * * *   COCKPIT  * * * * * * */
  /* * * * * * * * * *  * * * * * * * * */
  Capability.SHOW_MY_MESSAGES_TILE,
  Capability.SHOW_MY_PROJECTS_TILE,
  Capability.SHOW_MY_APPOINTMENTS_TILE,
  Capability.SHOW_MY_HY_DRY_DEVICES_TILE,
  Capability.SHOW_PROJECT_ORGRANISATION_TILE,
  Capability.SHOW_APPOINTMENT_MANAGEMENTILE_TILE,
  /* * * * * * * * * *  * * * * * * * * */

  /* * * * * * * * * *  * * * * * * * * */
  /* * * * * * *  SETTINGS  * * * * * * */
  /* * * * * * * * * *  * * * * * * * * */
  Capability.VIEW_PROJECT_SETTINGS,
  Capability.VEHICLE_MANAGEMENT,
  Capability.DEVICE_CHECK_MANAGEMENT,
  Capability.ROLE_MANAGEMENT,
  // Released to clients on 2026-09-07 (ticket 2609016). Granted to every user rather than resolved
  // per role: anyone who can see an address should be able to see where it is, and what a client
  // shows is already decided twice over by KARTE and by its own `Adressen geocodieren` switch.
  // `VIEW_MAP_PAGE`, the standalone Karte screen, stays IRES-only.
  Capability.GEO_ENCODING,

  /* * * * * * * * * *  * * * * * * * * */
  /* * * *   HY-DRY EVENT LOGS  * * * * */
  /* * * * * * * * * *  * * * * * * * * */
  Capability.SEE_SENSOR_EVENT_LOGS,
  /* * * * * * * * * *  * * * * * * * * */

  /* * * * * * * * * *  * * * * * * * * */
  /* * * * *  HY-DRY DEVICES  * * * * * */
  /* * * * * * * * * *  * * * * * * * * */
  Capability.VIEW_POWER_INFORMATION,
  Capability.VIEW_WEATHER_INFORMATION,
  /* * * * * * * * * *  * * * * * * * * */
];

// Capabilities that are either exclusive or always enabled for IRES users
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const EXCLUSIVE_IRES_CAPABILITIES = [
  /* * * * * * * * * *  * * * * * * * * */
  /* * * * * * *  PROJECTS  * * * * * * */
  /* * * * * * * * * *  * * * * * * * * */
  Capability.MOVE_PROJECT,
  Capability.SPECIAL_RIGHTS_FOR_PROJECT_TYPE_SELECTION,
  Capability.ALWAYS_ALLOWED_TO_CONCLUDE_PROJECT,                  // Only if project has no devices assigned to it
  Capability.ALWAYS_ALLOWED_TO_EDIT_PROJECT,
  Capability.ALWAYS_ALLOWED_TO_VIEW_TIME_TRACKING,
  Capability.ALWAYS_ALLOWED_TO_EDIT_DEVICE_ASSIGNMENTS,
  Capability.ALWAYS_ALLOWED_TO_CORRECT_METER_READINGS,
  Capability.ALWAYS_ALLOWED_TO_ASSIGN_DEVICES,
  Capability.SKIP_ASSIGNMENT_DATA_VALIDATION,
  Capability.DEFINE_PROJECT_TYPES,
  Capability.ALLOWED_TO_SEE_PROJECT_APP_VERSION,
  /* * * * * * * * * *  * * * * * * * * */

  /* * * * * * * * * *  * * * * * * * * */
  /* * * * *  HY-DRY DEVICES  * * * * * */
  /* * * * * * * * * *  * * * * * * * * */
  Capability.SEARCH_ANY_HY_DRY_DEVICE_ICON,
  Capability.VIEW_POWER_INFORMATION,
  Capability.VIEW_WEATHER_INFORMATION,
  Capability.VIEW_INTENSITY,
  Capability.VIEW_POWER_ON_BUTTON,              // For ComBox
  Capability.VIEW_TEST_RELAYS_BUTTON,           // For ComBox
  Capability.IOT_COMMANDS_ACCESS,               // For ComBox
  Capability.SHOW_ALL_INFO,                     // For ComBox
  Capability.SHOW_OPEN_SETTINGS_ICON,           
  Capability.SHOW_ADDITIONAL_DEVICE_INFO,       // Connect reason, program step cycle, no. of runs, event, heating information

  Capability.SHOW_PROGRAM_QUALITY_RATING,
  Capability.VIEW_HY_DRY_DEVICE_CONTROL,
  Capability.VIEW_HY_DRY_DEVICE_DETAILS_OF_ALL_CLIENTS,
  Capability.VIEW_RUNS,
  Capability.VIEW_RUN_DETAILS,
  Capability.VIEW_RUN_STARTS_CAUSED_BY_MAX_RUN_DURATION,

  Capability.MARK_END_DATE_MANUAL_CORRECTIONS,

  /* * * *   HY-DRY EVENT LOGS  * * * * */
  Capability.SEE_LID_AND_PAUSE_EVENT_LOGS,
  Capability.SEE_ALL_EVENT_LOGS,                // Logs before 2023, skip firmware requirements, see event numbers only visible for IRES
  Capability.TOGGLE_STEP_EVENT_LOGS,            // For Panel
  Capability.VIEW_CYCLE_INFO,
  Capability.VIEW_STEP_INFO,
  Capability.VIEW_LIMIT_LINES_FOR_RUNS,

  /* * * * * *  DRYING HISTORY  * * * * */
  Capability.VIEW_DRYING_HISTORY_OF_ALL_CLIENTS,
  /* * * * * * * * * *  * * * * * * * * */

  /* * * * * * * * * *  * * * * * * * * */
  /* * * * * * *  MESSAGES *  * * * * * */
  /* * * * * * * * * *  * * * * * * * * */
  Capability.SEE_ALL_MESSAGES,
  /* * * * * * * * * *  * * * * * * * * */

  /* * * * * * * * * *  * * * * * * * * */
  /* * * * * * *  SETTINGS  * * * * * * */
  /* * * * * * * * * *  * * * * * * * * */
  Capability.VEHICLE_MANAGEMENT,
  Capability.DEVICE_CHECK_MANAGEMENT,
  Capability.VIEW_MAP_PAGE,
  Capability.VIEW_MIRA,
  Capability.VIEW_MIRA_PERCENTILES,
  Capability.VIEW_MIRA_MARKET_CLIENTS,
  Capability.VIEW_MIRA_MARKET_COUNTRIES,

  Capability.VIEW_SYSTEM_SETTINGS,
  Capability.VIEW_SYSTEM_CONFIGURATIONS,
  Capability.VIEW_IRES_SETTINGS,
  Capability.VIEW_TIME_TRACKING_SETTINGS,
  Capability.VIEW_SCHEDULING_SETTINGS,
  Capability.VIEW_DEVICE_MANAGER_SETTINGS,

  Capability.SYSTEM_CONFIGURATION_DISTRIBUTION,
  /* * * * * * * * * *  * * * * * * * * */

  /* * * * * * * * * *  * * * * * * * * */
  /* * * * *  USER MANAGEMENT * * * * * */
  /* * * * * * * * * *  * * * * * * * * */
  Capability.ADMIN_RIGHTS,
  Capability.EDIT_SHARED_EMAIL_ADDRESS,
  Capability.SKIP_SETTING_SESSION_TOKEN,
  Capability.GRANT_ALL_LICENCES,
  Capability.SELECT_DEV_LANGUAGES,
  Capability.DEFINE_SHARED_USER_MASTER_CLIENT_ACCESS,
  Capability.SKIP_USER_DATA_VALIDATION,
  /* * * * * * * * * *  * * * * * * * * */

  /* * * * * * * * * *  * * * * * * * * */
  /* * * * *  ADMINISTRATION  * * * * * */
  /* * * * * * * * * *  * * * * * * * * */
  Capability.DEFINE_SYSTEM_CONFIGURATION,
  Capability.VIEW_EXTERNAL_SYSTEMS,
  /* * * * * * * * * *  * * * * * * * * */
];

/**
 * Rights to change something. While "Sicht auf" shows another user, nobody has them, whatever their
 * role, IRES or admin status (ticket 2610051: strictly read-only), so every button behind them goes.
 *
 * Left out on purpose: rights that also decide whether a whole page or menu entry shows (vehicles,
 * roles, device import and checks, member clients, the administrator client configuration). Turning
 * those off would hide the page instead of making it read-only; their buttons are switched off on
 * the page, and the write guard (`ViewAsWriteGuard`) holds behind everything. TOGGLE_DARK_MODE too:
 * without it Scaffold forces the light theme. And rights that only change what is displayed
 * (REMOVE_OUTLIER_SAMPLES, MARK_END_DATE_MANUAL_CORRECTIONS): the view shows them as the chosen user
 * sees them (ticket 2610052).
 */
export const VIEW_AS_BLOCKED_CAPABILITIES: Capability[] = [
  Capability.CREATE_PROJECT,
  Capability.REOPEN_COMPLETED_PHASES,
  Capability.EDIT_PROJECT,
  Capability.CREATE_APPOINTMENT,
  Capability.DELETE_PROJECT,
  Capability.RESTORE_PROJECT,
  Capability.MOVE_PROJECT,
  Capability.DEFINE_PROJECT_TYPES,
  Capability.ALWAYS_ALLOWED_TO_CONCLUDE_PROJECT,
  Capability.ALWAYS_ALLOWED_TO_EDIT_PROJECT,
  Capability.ALWAYS_ALLOWED_TO_EDIT_DEVICE_ASSIGNMENTS,
  Capability.ALWAYS_ALLOWED_TO_CORRECT_METER_READINGS,
  Capability.ALWAYS_ALLOWED_TO_ASSIGN_DEVICES,
  Capability.SKIP_ASSIGNMENT_DATA_VALIDATION,
  Capability.EDIT_HY_DRY_DEVICES,
  Capability.CONTROL_COMBOX_VIA_IOT,
  Capability.VIEW_POWER_ON_BUTTON,
  Capability.VIEW_TEST_RELAYS_BUTTON,
  Capability.IOT_COMMANDS_ACCESS,
  Capability.SHOW_OPEN_SETTINGS_ICON,
  Capability.FORCE_LOAD_RUNS_VIA_IOT,
  Capability.VIEW_HY_DRY_DEVICE_CONTROL,
  Capability.CHANGE_PROGRAM_DURING_DRYING,
  Capability.EDIT_ALL_DEVICES,
  Capability.EDIT_IRES_CATEGORIES_AND_TEMPLATES,
  Capability.CREATE_USER,
  Capability.DELETE_USER,
  Capability.EDIT_ADMIN,
  Capability.EDIT_OWN_ADMIN,
  Capability.EDIT_USER,
  Capability.EDIT_VALIDATION,
  Capability.EDIT_SHARED_EMAIL_ADDRESS,
  Capability.GRANT_ALL_LICENCES,
  Capability.DEFINE_SHARED_USER_ACCESS,
  Capability.DEFINE_SHARED_USER_MASTER_CLIENT_ACCESS,
  Capability.MOVE_DEVICES_TO_CLIENT,
  Capability.EDIT_DATA_VIEWER,
];

function hasCapability(
  activeUser: User | null,
  userCapability: Capability,
  ...capabilities: UserCapability[]
) {
  // Read first, so a computed built on any access function re-evaluates when "Sicht auf" changes.
  if (isViewAsSelected() && VIEW_AS_BLOCKED_CAPABILITIES.includes(userCapability)) {
    return false;
  }

  // Always ativate IRES capabilities for these capabilities regardless of the configuration of
  // 'Activate special rights for IRES users?'
  const alwaysActivateIresCapabilities =
    userCapability === Capability.VIEW_IRES_SETTINGS ||
    userCapability === Capability.SKIP_SETTING_SESSION_TOKEN;

  const hasIresCapabilities =
    isIresAdminAccount(activeUser, alwaysActivateIresCapabilities) ||
    isIresClient(alwaysActivateIresCapabilities);

  const hasAdminCapabilities = isAdmin(activeUser);

  if (IRES_CAPABILITIES.includes(userCapability) && hasIresCapabilities) {
    return true;
  }
  else if (ADMIN_CAPABILITIES.includes(userCapability) && hasAdminCapabilities) {
    return true;
  }
  else {
    return (
      doesUserHaveCapability(activeUser, ...capabilities) ||
      GENERAL_CAPABILITIES.includes(userCapability)
    );
  }
}

/* * * * * * * * * *  * * * * * * * * */
/* * * * * * *   COCKPIT  * * * * * * */
/* * * * * * * * * *  * * * * * * * * */
export function switchTargetUserAccess(activeUser: User | null) {
  return hasCapability(
    activeUser,
    Capability.SWITCH_TARGET_USER,

    UserCapability.OFFICE,
    UserCapability.PROJECT_MANAGEMENT
  );
}

export function myMessagesTileAccess(activeUser: User | null) {
  return (
    manageMessagesClientAccess() &&
    hasCapability(
      activeUser,
      Capability.SHOW_MY_MESSAGES_TILE
    )
  );
}

export function myProjectsTileAccess(activeUser: User | null) {
  return (
    manageProjectsClientAccess(activeUser) &&
    hasCapability(
      activeUser,
      Capability.SHOW_MY_PROJECTS_TILE
    )
  );
}

export function relevantProjectsTileAccess(activeUser: User | null) {
  return (
    manageProjectsClientAccess(activeUser) &&
    hasCapability(
      activeUser,
      Capability.SHOW_RELEVANT_PROJECTS_TILE,

      UserCapability.OFFICE,
      UserCapability.PROJECT_MANAGEMENT
    )
  );
}

export function allProjectsTileAccess(activeUser: User | null) {
  return (
    manageProjectsClientAccess(activeUser) &&
    hasCapability(
      activeUser,
      Capability.SHOW_ALL_PROJECTS_TILE,

      UserCapability.SEE_ALL_PROJECTS,
      UserCapability.OFFICE,
      UserCapability.PROJECT_MANAGEMENT
    )
  );
}

export function projectOraganisationTileAccess(activeUser: User | null) {
  return (
    manageProjectsClientAccess(activeUser) &&
    hasCapability(
      activeUser,
      Capability.SHOW_PROJECT_ORGRANISATION_TILE
    )
  );
}

export function myAppointmentsTileAccess(activeUser: User | null) {
  return (
    manageProjectsClientAccess(activeUser) &&
    hasCapability(
      activeUser,
      Capability.SHOW_MY_APPOINTMENTS_TILE
    )
  );
}

export function appointmentManagementTileAccess(activeUser: User | null) {
  return (
    manageProjectsClientAccess(activeUser) &&
    hasCapability(
      activeUser,
      Capability.SHOW_APPOINTMENT_MANAGEMENTILE_TILE
    )
  );
}

export function myHyDryDevicesTileAccess(activeUser: User | null) {
  return (
    manageHyDryDevicesClientAccess() &&
    hasCapability(
      activeUser,
      Capability.SHOW_MY_HY_DRY_DEVICES_TILE
    )
  );
}

export function allDevicesTileAccess(activeUser: User | null) {
  return (
    manageAllDevicesClientAccess() &&
    getShowDeviceManagementInCockpitAccess() &&
    hasCapability(
      activeUser,
      Capability.SHOW_ALL_DEVICES_TILE,

      UserCapability.EDIT_DEVICES
    )
  );
}
/* * * * * * * * * *  * * * * * * * * */

/* * * * * * * * * *  * * * * * * * * */
/* * * * * * *  PROJECTS  * * * * * * */
/* * * * * * * * * *  * * * * * * * * */
export function seeAllProjectsAccess(activeUser: User | null) {
  return (
    manageProjectsClientAccess(activeUser) &&
    hasCapability(
      activeUser,
      Capability.SEE_ALL_PROJECTS,

      UserCapability.SEE_ALL_PROJECTS,
      UserCapability.OFFICE,
      UserCapability.PROJECT_MANAGEMENT
    )
  );
}

export function createAppointmentAccess(
  activeUser: User | null,
  projectSettings: ProjectSettingsV2
) {
  const isAllowedToCreateAppointmentsInPortal = projectSettings.newAppointmentsIn.includes(ProjectSettingsOptions.PORTAL);

  return (
    !ClientModule.isAdministratorClientUser &&
    isAllowedToCreateAppointmentsInPortal &&
    getErpIntegration() !== ErpIntegration.EXCLUSIVE &&
    manageProjectsClientAccess(activeUser) &&
    hasCapability(
      activeUser,
      Capability.CREATE_APPOINTMENT,

      UserCapability.EDIT_PROJECT
    )
  );
}

export function createProjectAccess(
  activeUser: User | null,
  projectSettings: ProjectSettingsV2
) {
  const isAllowedToCreateProjectsInPortal = projectSettings.newProjectsIn.includes(ProjectSettingsOptions.PORTAL);

  return (
    !ClientModule.isAdministratorClientUser &&
    isAllowedToCreateProjectsInPortal &&
    getErpIntegration() !== ErpIntegration.EXCLUSIVE &&
    manageProjectsClientAccess(activeUser) &&
    hasCapability(
      activeUser,
      Capability.CREATE_PROJECT,

      UserCapability.CREATE_PROJECT
    )
  );
}

export function editProjectAccess(activeUser: User | null) {
  return (
    !ClientModule.isAdministratorClientUser &&
    manageProjectsClientAccess(activeUser) &&
    hasCapability(
      activeUser,
      Capability.EDIT_PROJECT,

      UserCapability.EDIT_PROJECT
    )
  );
}

export function reopenProjectAccess(activeUser: User | null) {
  return (
    !ClientModule.isAdministratorClientUser &&
    manageProjectsClientAccess(activeUser) &&
    hasCapability(
      activeUser,
      Capability.REOPEN_COMPLETED_PHASES,

      UserCapability.REOPEN_COMPLETED_PHASES
    )
  )
}

export function deleteProjectAccess(activeUser: User | null) {
  return (
    !ClientModule.isAdministratorClientUser &&
    manageProjectsClientAccess(activeUser) &&
    hasCapability(
      activeUser,
      Capability.DELETE_PROJECT,

      UserCapability.DELETE_PROJECT
    )
  );
}

export function leakDetectionProjectsAccess(activeUser: User | null) {
  return (
    manageProjectsClientAccess(activeUser) &&
    hasCapability(
      activeUser,
      Capability.LEAK_DETECTION_PROJECTS,

      UserCapability.LEAK_DETECTION
    )
  );
}

export function dryingProjectsAccess(activeUser: User | null) {
  return (
    manageProjectsClientAccess(activeUser) &&
    hasCapability(
      activeUser,
      Capability.DRYING_PROJECTS,

      UserCapability.DRYING
    )
  );
}

export function officeAccess(activeUser: User | null) {
  return (
    manageProjectsClientAccess(activeUser) &&
    hasCapability(
      activeUser,
      Capability.OFFICE_PROJECTS,

      UserCapability.OFFICE
    )
  );
}

export function projectManagementAccess(activeUser: User | null) {
  return (
    manageProjectsClientAccess(activeUser) &&
    hasCapability(
      activeUser,
      Capability.PROJECT_MANAGEMENT,

      UserCapability.PROJECT_MANAGEMENT
    )
  );
}

export function projectSupervisionAccess(activeUser: User | null) {
  return (
    manageProjectsClientAccess(activeUser) &&
    hasCapability(
      activeUser,
      Capability.PROJECT_SUPERVISION,

      UserCapability.PROJECT_SUPERVISION
    )
  );
}

export function damageAssessmentAndLeakDetectionProjectsAccess(activeUser: User | null) {
  return (
    manageProjectsClientAccess(activeUser) &&
    hasCapability(
      activeUser,
      Capability.DAMAGE_ASSESSMENT_AND_LEAK_DETECTION_PROJECTS,

      UserCapability.DAMAGE_ASSESSMENT_AND_LEAK_DETECTION
    )
  );
}

export function dryingAndRepairProjectsAccess(activeUser: User | null) {
  return (
    manageProjectsClientAccess(activeUser) &&
    hasCapability(
      activeUser,
      Capability.DRYING_AND_REPAIR_PROJECTS,

      UserCapability.DRYING_AND_REPAIR
    )
  );
}

export function managerProjectsAccess(activeUser: User | null) {
  return (
    manageProjectsClientAccess(activeUser) &&
    hasCapability(
      activeUser,
      Capability.MANAGER_PROJECTS,

      UserCapability.OFFICE,
      UserCapability.PROJECT_MANAGEMENT
    )
  );
}

export function specialRightsForProjectTypeSelectionAccess(activeUser: User | null) {
  return (
    manageProjectsClientAccess(activeUser) &&
    hasCapability(
      activeUser,
      Capability.SPECIAL_RIGHTS_FOR_PROJECT_TYPE_SELECTION
    )
  );
}

export function alwaysAllowedToConcludeProjectAccess(activeUser: User | null) {
  return (
    !ClientModule.isAdministratorClientUser &&
    manageProjectsClientAccess(activeUser) &&
    hasCapability(
      activeUser,
      Capability.ALWAYS_ALLOWED_TO_CONCLUDE_PROJECT
    )
  );
}

export function alwaysAllowedToEditProjectAccess(activeUser: User | null) {
  return (
    manageProjectsClientAccess(activeUser) &&
    hasCapability(
      activeUser,
      Capability.ALWAYS_ALLOWED_TO_EDIT_PROJECT
    )
  );
}

export function alwaysAllowedToEditDeviceAssignmentsAccess(activeUser: User | null) {
  return (
    !ClientModule.isAdministratorClientUser &&
    manageProjectsClientAccess(activeUser) &&
    hasCapability(
      activeUser,
      Capability.ALWAYS_ALLOWED_TO_EDIT_DEVICE_ASSIGNMENTS
    )
  );
}

export function alwaysAllowedToCorrectMeterReadingsAccess(activeUser: User | null) {
  return (
    !ClientModule.isAdministratorClientUser &&
    manageProjectsClientAccess(activeUser) &&
    hasCapability(
      activeUser,
      Capability.ALWAYS_ALLOWED_TO_CORRECT_METER_READINGS
    )
  );
}

export function alwaysAllowedToAssignDevicesAccess(activeUser: User | null) {
  return (
    !ClientModule.isAdministratorClientUser &&
    manageProjectsClientAccess(activeUser) &&
    hasCapability(
      activeUser,
      Capability.ALWAYS_ALLOWED_TO_ASSIGN_DEVICES
    )
  );
}

export function skipAssignmentDataValidationAccess(activeUser: User | null) {
  return (
    !ClientModule.isAdministratorClientUser &&
    manageProjectsClientAccess(activeUser) &&
    hasCapability(
      activeUser,
      Capability.SKIP_ASSIGNMENT_DATA_VALIDATION
    )
  );
}

export function viewArchivedProjectSettingsAccess(activeUser: User | null) {
  return (
    manageProjectsClientAccess(activeUser) &&
    hasCapability(
      activeUser,
      Capability.VIEW_ARCHIVED_PROJECTS
    )
  )
}

export function restoreProjectAccess(activeUser: User | null) {
  return (
    !ClientModule.isAdministratorClientUser &&
    manageProjectsClientAccess(activeUser) &&
    hasCapability(
      activeUser,
      Capability.RESTORE_PROJECT
    )
  )
}

/** Whether the user can move a project to finished state or reopen a finished project. IRES-exclusive — requires {@link Capability.MOVE_PROJECT}. */
export function moveProjectAccess(activeUser: User | null) {
  return (
    !ClientModule.isAdministratorClientUser &&
    manageProjectsClientAccess(activeUser) &&
    hasCapability(
      activeUser,
      Capability.MOVE_PROJECT
    )
  )
}

export function defineProjectTypesAccess(activeUser: User | null) {
  return (
    !ClientModule.isAdministratorClientUser &&
    manageProjectsClientAccess(activeUser) &&
    hasCapability(
      activeUser,
      Capability.DEFINE_PROJECT_TYPES
    )
  )
}

export function defineProjectAppVersionVisibilityAccess(activeUser: User | null) {
  return (
    !ClientModule.isAdministratorClientUser &&
    manageProjectsClientAccess(activeUser) &&
    hasCapability(
      activeUser,
      Capability.ALLOWED_TO_SEE_PROJECT_APP_VERSION
    )
  )
}
/* * * * * * * * * *  * * * * * * * * */

/* * * * * * * * * *  * * * * * * * * */
/* * * * *  HY-DRY DEVICES  * * * * * */
/* * * * * * * * * *  * * * * * * * * */
export function searchAnyHyDryDeviceAccess(activeUser: User | null) {
  return (
    manageHyDryDevicesClientAccess() &&
    hasCapability(
      activeUser,
      Capability.SEARCH_ANY_HY_DRY_DEVICE_ICON
    )
  );
}

export function allHyDryDevicesAccess(activeUser: User | null) {
  if (activeUser) {
    return (
      manageHyDryDevicesClientAccess() &&
      (
        hasCapability(
          activeUser,
          Capability.SEE_ALL_HY_DRY_DEVICES,

          UserCapability.SEE_ALL_PROJECTS,
          UserCapability.OFFICE,
          UserCapability.PROJECT_MANAGEMENT,
          UserCapability.EDIT_DEVICES,
          UserCapability.DEVICE_ADMINISTRATION                                       
        ) ||
        (activeUser.licenceInfo?.licencesV3 &&
          (activeUser.licenceInfo?.licencesV3?.length ?? 0) == 0) ||
        (!activeUser.licenceInfo?.licencesV3 &&
          (activeUser.licenceInfo?.licences?.length ?? 0) == 0)
      )
    );
  } else {
    return true;
  }
}

export function editHyDryDevicesAccess(activeUser: User | null) {
  return (
    manageHyDryDevicesClientAccess() &&
    hasCapability(
      activeUser,
      Capability.EDIT_HY_DRY_DEVICES,

      UserCapability.EDIT_DEVICES
    )
  )
}

export function viewPowerInformationAccess(activeUser: User | null) {
  return hasCapability(
    activeUser,
    Capability.VIEW_POWER_INFORMATION
  );
}

export function viewWeatherInformationAccess(activeUser: User | null) {
  return hasCapability(
    activeUser,
    Capability.VIEW_WEATHER_INFORMATION
  );
}

export function viewIntensityAccess(activeUser: User | null) {
  return (
    manageHyDryDevicesClientAccess() &&
    hasCapability(
      activeUser,
      Capability.VIEW_INTENSITY
    )
  );
}

export function controlComBoxViaIotAccess(activeUser: User | null) {
  return (
    manageHyDryDevicesClientAccess() &&
    hasCapability(
      activeUser,
      Capability.CONTROL_COMBOX_VIA_IOT,

      UserCapability.PROJECT_MANAGEMENT
    )
  );
}

export function viewPowerOnButtonAccess(activeUser: User | null) {
  return (
    manageHyDryDevicesClientAccess() &&
    hasCapability(
      activeUser,
      Capability.VIEW_POWER_ON_BUTTON
    )
  );
}

export function viewTestRelaysButtonAccess(activeUser: User | null) {
  return (
    manageHyDryDevicesClientAccess() &&
    hasCapability(
      activeUser,
      Capability.VIEW_TEST_RELAYS_BUTTON
    )
  );
}

export function iotCommandsAccess(activeUser: User | null) {
  return (
    manageHyDryDevicesClientAccess() &&
    hasCapability(
      activeUser,
      Capability.IOT_COMMANDS_ACCESS
    )
  );
}

export function showAllInfoAccess(activeUser: User | null) {
  return (
    manageHyDryDevicesClientAccess() &&
    hasCapability(
      activeUser,
      Capability.SHOW_ALL_INFO
    )
  );
}

export function showOpenSettingsIconAccess(activeUser: User | null) {
  return (
    manageHyDryDevicesClientAccess() &&
    hasCapability(
      activeUser,
      Capability.SHOW_OPEN_SETTINGS_ICON
    )
  );
}

export function showAdditionalDeviceInfoAccess(activeUser: User | null) {
  return (
    manageHyDryDevicesClientAccess() &&
    hasCapability(
      activeUser,
      Capability.SHOW_ADDITIONAL_DEVICE_INFO
    )
  );
}

export function showProgramQualityRatingAccess(activeUser: User | null) {
  return (
    manageHyDryDevicesClientAccess() &&
    hasCapability(
      activeUser,
      Capability.SHOW_PROGRAM_QUALITY_RATING
    )
  );
}

export function viewHyDryDeviceControlAccess(activeUser: User | null) {
  return (
    manageHyDryDevicesClientAccess() &&
    hasCapability(
      activeUser,
      Capability.VIEW_HY_DRY_DEVICE_CONTROL
    )
  );
}

export function viewHyDryDeviceDetailsOfAllClientsAccess(activeUser: User | null) {
  return (
    manageHyDryDevicesClientAccess() &&
    hasCapability(
      activeUser,
      Capability.VIEW_HY_DRY_DEVICE_DETAILS_OF_ALL_CLIENTS
    )
  );
}

/** Whether the user can read project details from another client's Firestore path. */
export function viewProjectDetailsOfAnotherClientAccess(activeUser: User | null) {
  return (
    manageHyDryDevicesClientAccess() &&
    hasCapability(
      activeUser,
      Capability.VIEW_PROJECT_DETAILS_OF_ANOTHER_CLIENT
    )
  );
}

export function viewRunsAccess(activeUser: User | null) {
  return (
    manageHyDryDevicesClientAccess() &&
    hasCapability(
      activeUser,
      Capability.VIEW_RUNS
    )
  );
}

export function viewRunDetailsAccess(activeUser: User | null) {
  return (
    manageHyDryDevicesClientAccess() &&
    hasCapability(
      activeUser,
      Capability.VIEW_RUN_DETAILS
    )
  );
}

export function viewRunStartsCausedByMaxRunDurationAccess(activeUser: User | null) {
  return (
    manageHyDryDevicesClientAccess() &&
    hasCapability(
      activeUser,
      Capability.VIEW_RUN_STARTS_CAUSED_BY_MAX_RUN_DURATION
    )
  );
}

export function markEndDateManualCorrectionsAccess(activeUser: User | null) {
  return (
    manageHyDryDevicesClientAccess() &&
    hasCapability(
      activeUser,
      Capability.MARK_END_DATE_MANUAL_CORRECTIONS
    )
  );
}

export function removeOutlierSamplesAccess(activeUser: User | null) {
  return (
    manageHyDryDevicesClientAccess() &&
    hasCapability(
      activeUser,
      Capability.REMOVE_OUTLIER_SAMPLES
    )
  );
}

export function forceLoadRunsViaIotAccess(activeUser: User | null) {
  return (
    manageHyDryDevicesClientAccess() &&
    hasCapability(
      activeUser,
      Capability.FORCE_LOAD_RUNS_VIA_IOT
    )
  );
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function changeProgramDuringDryingAccess(activeUser: User | null) {
  return false; // This capability is currently not used, and the access is blocked for all users. The function is kept for future use when this capability might be needed again.

  /* return (
    manageHyDryDevicesClientAccess() &&
    hasCapability(
      activeUser,
      Capability.CHANGE_PROGRAM_DURING_DRYING,

      UserCapability.PROJECT_MANAGEMENT
    )
  ); */
}

/* * * *   HY-DRY EVENT LOGS  * * * * */
export function seeLidAndPauseEventLogsAccess(activeUser: User | null) {
  return (
    manageHyDryDevicesClientAccess() &&
    hasCapability(
      activeUser,
      Capability.SEE_LID_AND_PAUSE_EVENT_LOGS
    )
  );
}

export function seeSensorEventLogsAccess(activeUser: User | null) {
  return (
    manageHyDryDevicesClientAccess() &&
    hasCapability(
      activeUser,
      Capability.SEE_SENSOR_EVENT_LOGS
    )
  );
}

export function seeAllEventLogsAccess(activeUser: User | null) {
  return (
    manageHyDryDevicesClientAccess() &&
    hasCapability(
      activeUser,
      Capability.SEE_ALL_EVENT_LOGS
    )
  );
}

export function toggleStepEventLogsAccess(activeUser: User | null) {
  return (
    manageHyDryDevicesClientAccess() &&
    hasCapability(
      activeUser,
      Capability.TOGGLE_STEP_EVENT_LOGS
    )
  );
}

export function viewCycleInformationAccess(activeUser: User | null) {
  return (
    manageHyDryDevicesClientAccess() &&
    hasCapability(
      activeUser,
      Capability.VIEW_CYCLE_INFO
    )
  );
}

export function viewStepInformationAccess(activeUser: User | null) {
  return (
    manageHyDryDevicesClientAccess() &&
    hasCapability(
      activeUser,
      Capability.VIEW_STEP_INFO
    )
  );
}

export function viewLimitLinesForRunsAccess(activeUser: User | null) {
  return (
    manageHyDryDevicesClientAccess() &&
    hasCapability(
      activeUser,
      Capability.VIEW_LIMIT_LINES_FOR_RUNS
    )
  );
}

/* * * * * *  DRYING HISTORY  * * * * */
export function viewDryingHistoryOfAllClientsAccess(activeUser: User | null) {
  return (
    manageHyDryDevicesClientAccess() &&
    hasCapability(
      activeUser,
      Capability.VIEW_DRYING_HISTORY_OF_ALL_CLIENTS
    )
  );
}
/* * * * * * * * * *  * * * * * * * * */

/* * * * * * * * * *  * * * * * * * * */
/* * * * * *  ALL DEVICES   * * * * * */
/* * * * * * * * * *  * * * * * * * * */
export function editAllDevicesAccess(activeUser: User | null) {
  return (
    manageAllDevicesClientAccess() &&
    hasCapability(
      activeUser,
      Capability.EDIT_ALL_DEVICES,

      UserCapability.EDIT_DEVICES
    )
  )
}
/* * * * * * * * * *  * * * * * * * * */

/* * * * * * * * * *  * * * * * * * * */
/* * * * * * *  MESSAGES *  * * * * * */
/* * * * * * * * * *  * * * * * * * * */
export function seeAllMessagesAccess(activeUser: User | null) {
  return (
    manageMessagesClientAccess() &&
    hasCapability(
      activeUser,
      Capability.SEE_ALL_MESSAGES
    )
  )
}
/* * * * * * * * * *  * * * * * * * * */

/* * * * * * * * * *  * * * * * * * * */
/* * * * * * *  SETTINGS  * * * * * * */
/* * * * * * * * * *  * * * * * * * * */
export function viewProjectSettingsAccess(activeUser: User | null) {
  return (
    manageProjectManagementSettingsClientAccess(activeUser) &&
    hasCapability(activeUser, Capability.VIEW_PROJECT_SETTINGS)
  );
}

export function userSettingsAccess(activeUser: User | null) {
  return hasCapability(
    activeUser,
    Capability.VIEW_USER_SETTINGS,

    UserCapability.EDIT_ADMIN,
    UserCapability.EDIT_USER,
    UserCapability.EDIT_VALIDATION,
    UserCapability.SEE_ALL_SETTINGS
  );
}

export function companySettingsAccess(activeUser: User | null) {
  return hasCapability(
    activeUser,
    Capability.VIEW_COMPANY_SETTINGS,

    UserCapability.SEE_ALL_SETTINGS
  );
}

export function reportSettingsAccess(activeUser: User | null) {
  return (
    (
      manageProjectManagementSettingsClientAccess(activeUser) ||
      defineOwnReportSettingsClientAccess() ||
      defineReportSettingsForGroupClientAccess()

    ) &&
    hasCapability(
      activeUser,
      Capability.VIEW_REPORT_SETTINGS,

      UserCapability.SEE_ALL_SETTINGS
    )
  );
}

export function hyDryDeviceSettingsAccess(activeUser: User | null) {
  return (
    manageHyDryDevicesClientAccess() &&
    hasCapability(
      activeUser,
      Capability.VIEW_HY_DRY_DEVICE_SETTINGS,

      UserCapability.SEE_ALL_SETTINGS,
      UserCapability.EDIT_DEVICE_SETTINGS
    )
  );
}

export function otherDeviceSettingsAccess(activeUser: User | null) {
  return (
    manageAllDevicesClientAccess() &&
    hasCapability(
      activeUser,
      Capability.VIEW_ALL_DEVICES_SETTINGS,

      UserCapability.EDIT_DEVICE_SETTINGS,
      UserCapability.IMPORT_DEVICES
    )
  );
}

export function messageSettingsAccess(activeUser: User | null) {
  return (
    (
      manageMessagesClientAccess() ||
      manageHyDryMessagesSettingsClientAccess()
    ) &&
    manageHyDryDevicesClientAccess() &&
    hasCapability(
      activeUser,
      Capability.VIEW_MESSAGE_SETTINGS,

      UserCapability.SEE_ALL_SETTINGS,
      UserCapability.EDIT_ADMIN,
      UserCapability.EDIT_USER
    )
  );
}

export function projectSettingsAccess(activeUser: User | null) {
  return (
    manageProjectManagementSettingsClientAccess(activeUser) &&
    hasCapability(
      activeUser,
      Capability.VIEW_PROJECT_SETTINGS,

      UserCapability.SEE_ALL_SETTINGS
    )
  );
}

export function systemSettingsSectionAccess(activeUser: User | null) {
  return hasCapability(
    activeUser,
    Capability.VIEW_SYSTEM_SETTINGS_SECTION,

    UserCapability.SEE_ALL_SETTINGS
  );
}

export function timeTrackingSettingsAccess(activeUser: User | null) {
  return (
    manageTimeTrackingClientAccess() &&
    hasCapability(
      activeUser,
      Capability.VIEW_TIME_TRACKING_SETTINGS,

      UserCapability.SEE_ALL_SETTINGS
    )
  );
}

/**
 * Whether the user may view time tracking history even when the system configuration
 * time-tracking feature toggle is disabled. Intended for IRES users only.
 */
export function alwaysAllowedToViewTimeTrackingAccess(activeUser: User | null) {
  return (
    !isDevicesOnlyWebsite() &&
    hasCapability(
      activeUser,
      Capability.ALWAYS_ALLOWED_TO_VIEW_TIME_TRACKING
    )
  );
}

/**
 * Whether the user may expand/collapse merged same-entry rows on the Time Tracking
 * History (Zeiterfassung) page. IRES-only: reuses the IRES-gated time-tracking view
 * capability so only IRES users can drill into the individual entries that were merged
 * for display. Standard clients still see the merged rows but cannot expand them.
 */
export function expandMergedTimeTrackingEntriesAccess(activeUser: User | null) {
  return alwaysAllowedToViewTimeTrackingAccess(activeUser);
}

export function schedulingSettingsAccess(activeUser: User | null) {
  return (
    manageSchedulingClientAccess() &&
    hasCapability(
      activeUser,
      Capability.VIEW_SCHEDULING_SETTINGS,

      UserCapability.SEE_ALL_SETTINGS
    )
  );
}

export function iresSettingsAccess(activeUser: User | null) {
  return hasCapability(activeUser, Capability.VIEW_IRES_SETTINGS);
}

/**
 * Whether the user is asked to replace a temporary password (set by a password reset) with their own:
 * every user but the shared IRES admin login, which "Als Mandant anmelden" signs in as and nobody owns.
 *
 * Deliberately not {@link iresSettingsAccess}: that capability is held by every user of an IRES-typed
 * client (the IRES Admin Client included), so those users were never asked.
 */
export function replaceTemporaryPasswordAccess(activeUser: User | null) {
  return !!activeUser && !isIresAdminAccount(activeUser, true);
}

/**
 * Whether the user may switch between light and dark appearance.
 *
 * Dark mode is IRES-only until it is released to clients. Callers must not only hide the toggle
 * but also force the light theme when this returns false: the stored preference falls back to the
 * OS `prefers-color-scheme`, so a client on a dark-themed OS would otherwise get dark mode with no
 * toggle to leave it. See `docs/operations/unreleased-features.md`.
 */
export function darkModeAccess(activeUser: User | null) {
  return hasCapability(activeUser, Capability.TOGGLE_DARK_MODE);
}

export function aiPredictionsAccess(activeUser: User | null) {
  return hasCapability(activeUser, Capability.VIEW_AI_PREDICTIONS);
}

/** Whether the user can see sensor diagnostic details (signal strength, battery, firmware, raw event logs). */
export function viewSensorDiagnosticsAccess(activeUser: User | null) {
  return hasCapability(activeUser, Capability.VIEW_SENSOR_DIAGNOSTICS);
}

export function usersConfigurationAccess(activeUser: User | null) {
  return (
    !isDevicesOnlyWebsite() &&
    hasCapability(
      activeUser,
      Capability.VIEW_USERS_CONFIGURATION_SETTINGS_ITEM,

      UserCapability.SEE_ALL_SETTINGS
    )
  );
}

export function vehicleManagementAccess(activeUser: User | null) {
  return (
    manageVehiclesClientAccess() &&
    hasCapability(activeUser, Capability.VEHICLE_MANAGEMENT)
  );
}

export function deviceCheckManagementAccess(activeUser: User | null) {
  return (
    manageDeviceCheckAccess() &&
    hasCapability(activeUser, Capability.DEVICE_CHECK_MANAGEMENT)
  );
}

/**
 * Whether this user sees the **address-position features**: the map pin beside every address, the
 * picker it opens, and the lookups they run.
 *
 * Deliberately no longer covers the standalone Karte screen, which has its own
 * {@link mapPageAccess}. The two shared one capability until 2026-09-07, which meant they could
 * only ship together: releasing the finished address features would have put the unfinished Karte
 * screen in every client's sidebar with them.
 *
 * **Released to clients on 2026-09-07.** `Capability.GEO_ENCODING` is in `GENERAL_CAPABILITIES` and
 * `ClientCapability.MANAGE_GEO_ENCODING` in `OPERATIONAL_CAPABILITIES`; both layers had to move,
 * because either alone still refuses. What a client sees is decided by KARTE and by its own
 * `Adressen geocodieren` switch, which is off until asked for.
 */
export function geoEncodingAccess(activeUser: User | null) {
  return (
    manageGeoEncodingClientAccess() &&
    hasCapability(activeUser, Capability.GEO_ENCODING)
  );
}

/**
 * Whether the **address-position features are shown in the foreground**: the pin beside every
 * address and the picker it opens (Hans, ticket 2609016).
 *
 * Two audiences, and they are answered separately rather than by one boolean chain.
 *
 * **The IRES admin account sees the feature always**, whatever the client has configured — neither
 * KARTE nor the client's own `Adressen geocodieren` switch applies. Hans's wording is unqualified
 * ("the GC feature is always visible for IRESADMIN (IRES priviledge)"), and the point of it is to
 * let IRES look at a client's positions *before* that client has turned anything on, which is
 * exactly the state a client with KARTE = NEIN is in.
 *
 * (This first shipped narrower, with the bypass applying only to the client's switch while KARTE
 * still gated it. That left an IRES admin seeing nothing on the 373 clients where KARTE is NEIN,
 * i.e. in almost every case the bypass exists for.)
 *
 * **Everyone else** needs KARTE, the capability, and the client to have asked for it.
 *
 * Geocoding itself depends on none of this: IRES Cloud positions addresses on write regardless, so
 * a client switching it on finds its addresses already placed. What this controls is who can see
 * them.
 */
export function addressPositionAccess(activeUser: User | null) {
  if (isIresAdminAccount(activeUser)) {
    // The portal shape is still respected: a devices-only site has no address screens to put a pin
    // on, and this is the same guard the client-level access function applies.
    return !isDevicesOnlyWebsite();
  }

  return geoEncodingAccess(activeUser) && getAddressGeocodingAccess();
}

/**
 * Whether this user sees the standalone **Karte** screen (`/main/maps`).
 *
 * Still under development and for testing only (Taimoor, 2026-09-07), so it stays IRES-only while
 * the address-position features around it become releasable.
 *
 * Enforced in two places, and it needs both: `Scaffold.vue` hides the navigation entry, and
 * `hasAccess()` refuses the route. Hiding the entry alone leaves `/main/maps` reachable by typing
 * the URL, which is this repo's own "settings visibility must match route guards" lesson applied
 * to a page rather than to a settings row.
 */
export function mapPageAccess(activeUser: User | null) {
  return (
    manageGeoEncodingClientAccess() &&
    hasCapability(activeUser, Capability.VIEW_MAP_PAGE)
  );
}

/**
 * Whether this user sees the **Mira** screens (`/main/mira` and below), by the client's Mira setting in
 * Systemkonfiguration (#2610141):
 *
 * - Nein: nobody, IRES included.
 * - Nur IRES (the default): IRES client users and IRES admin accounts (`VIEW_MIRA`), the latter being how
 *   IRES looks at a customer's figures, since a screen reads only the signed-in client.
 * - Ja: IRES as above, and the client's users whose role has B1 Basislizenz Projekte (Ja or Bedingt): the
 *   role capability their B licence is derived from, checked the usual two-layer way rather than through
 *   the licence record, which follows it only at the next user save (Taimoor, 2026-10-30).
 *
 * A front-end gate only: Mira's nightly run computes every client whatever the setting says.
 *
 * Enforced in two places, like {@link mapPageAccess}: `Scaffold.vue` hides the navigation entry and
 * `hasAccess()` refuses the routes.
 */
export function miraAccess(activeUser: User | null) {
  // Every reactive read first, so a computed built on this follows a change of any of them (ui-conventions.md).
  const mode = getMiraModeAccess();
  const iresAccess = hasCapability(activeUser, Capability.VIEW_MIRA);
  const clientAccess = hasCapability(activeUser, Capability.VIEW_MIRA, UserCapability.BASIC_LICENCE);
  return mode === "ON" ? clientAccess : mode === "IRES_ONLY" && iresAccess;
}

/**
 * Whether the user sees the P15 / P85 percentiles on the Mira screens (the spread under a tile, the
 * range behind the dot plot marks, the band on the Verlauf chart).
 *
 * Follows {@link miraAccess} exactly: IRES through {@link Capability.VIEW_MIRA_PERCENTILES}, which sits in
 * the same groups as `VIEW_MIRA`, and with the client's Mira on, its users whose role has B1 too (Taimoor,
 * 2026-10-30, #2610141: clients see the same figures as IRES).
 */
export function miraPercentilesAccess(activeUser: User | null) {
  // With Mira on for the client, its users see the same figures as IRES, percentiles included (#2610141).
  const mode = getMiraModeAccess();
  const iresAccess = hasCapability(activeUser, Capability.VIEW_MIRA_PERCENTILES);
  const clientAccess = hasCapability(activeUser, Capability.VIEW_MIRA_PERCENTILES, UserCapability.BASIC_LICENCE);
  return mode === "ON" ? clientAccess : mode === "IRES_ONLY" && iresAccess;
}

/**
 * Whether a Markt figure in Mira names how many Mandanten it is built from (Hans, #2611021): IRES users and
 * IRES admin accounts only ({@link Capability.VIEW_MIRA_MARKET_CLIENTS}), never a client's users, even with
 * the client's Mira on. Nobody where Mira is off.
 */
export function miraMarketClientsAccess(activeUser: User | null) {
  // Both reads first, so a computed built on this follows a change of either (ui-conventions.md).
  const mode = getMiraModeAccess();
  const iresAccess = hasCapability(activeUser, Capability.VIEW_MIRA_MARKET_CLIENTS);
  return mode !== "OFF" && iresAccess;
}

/**
 * Whether the Markt in Mira can be narrowed to one country (Hans, #2611022): IRES users and IRES admin
 * accounts only ({@link Capability.VIEW_MIRA_MARKET_COUNTRIES}). Nobody where Mira is off.
 */
export function miraMarketCountriesAccess(activeUser: User | null) {
  // Both reads first, so a computed built on this follows a change of either (ui-conventions.md).
  const mode = getMiraModeAccess();
  const iresAccess = hasCapability(activeUser, Capability.VIEW_MIRA_MARKET_COUNTRIES);
  return mode !== "OFF" && iresAccess;
}

export function roleManagementAccess(activeUser: User | null) {
  return (
    manageRolesClientAccess() &&
    hasCapability(
      activeUser,
      Capability.ROLE_MANAGEMENT,

      UserCapability.SEE_ALL_SETTINGS
    )
  );
}

export function branchManagementAccess() {
  return (
    manageBranchesClientAccess()
  );
}

/**
 * Controls the Gerätekategorien and Projektgeräte-Einstellungen pages under Settings > Alle Geräte.
 * Needs {@link allDevicesSettingsClientAccess}, so they go with "Alle Geräte verwalten" like the
 * rest of the section, except for a MASTER-Mandant defining them for its group.
 */
export function deviceSettingsItemAccess(activeUser: User | null) {
  return (
    allDevicesSettingsClientAccess() &&
    hasCapability(
      activeUser,
      Capability.VIEW_EDIT_DEVICE_SETTINGS_ITEM,

      UserCapability.EDIT_DEVICE_SETTINGS
    )
  );
}

/** Controls whether the user can edit names of IRES-owned categories and templates. */
export function editIresCategoriesAndTemplatesAccess(activeUser: User | null) {
  return hasCapability(
    activeUser,
    Capability.EDIT_IRES_CATEGORIES_AND_TEMPLATES
  );
}

export function importDevicesAccess(activeUser: User | null) {
  return (
    manageAllDevicesClientAccess() &&
    hasCapability(
      activeUser,
      Capability.IMPORT_DEVICES,

      UserCapability.IMPORT_DEVICES
    )
  );
}

export function messageSelectionAccess(activeUser: User | null) {
  return (
    manageHyDryDevicesClientAccess() &&
    hasCapability(
      activeUser,
      Capability.MESSAGE_SELECTION,

      UserCapability.SEE_ALL_SETTINGS
    )
  );
}

export function mailboxAccess(activeUser: User | null) {
  return (
    manageMessagesClientAccess() &&
    manageHyDryDevicesClientAccess() &&
    hasCapability(
      activeUser,
      Capability.VIEW_MAILBOX,

      UserCapability.EDIT_ADMIN,
      UserCapability.EDIT_USER,
      UserCapability.SEE_ALL_SETTINGS
    )
  );
}

export function systemSettingsAccess(activeUser: User | null) {
  return hasCapability(activeUser, Capability.VIEW_SYSTEM_SETTINGS);
}

export function systemConfigurationAccess(activeUser: User | null) {
  return hasCapability(activeUser, Capability.VIEW_SYSTEM_CONFIGURATIONS);
}

export function deviceManagerSettingsAccess(activeUser: User | null) {
  return hasCapability(activeUser, Capability.VIEW_DEVICE_MANAGER_SETTINGS);
}

export function viewFinishedProjectsSettingsAccess(activeUser: User | null) {
  return hasCapability(
    activeUser,
    Capability.VIEW_FINISHED_PROJECTS_SETTINGS,

    UserCapability.SEE_ALL_SETTINGS
  );
}
/* * * * * * * * * *  * * * * * * * * */

/* * * * * * * * * *  * * * * * * * * */
/* * * * *  USER MANAGEMENT * * * * * */
/* * * * * * * * * *  * * * * * * * * */
export function adminRightsAccess(activeUser: User | null) {
  return hasCapability(
    activeUser,
    Capability.ADMIN_RIGHTS
  );
}

export function selectDevLanguagesAccess(activeUser: User | null) {
  return hasCapability(
    activeUser,
    Capability.SELECT_DEV_LANGUAGES
  );
}

/**
 * Whether the user is a **named SysAdmin of the admin portal client**: on that client, with role
 * ADMIN ("SysAdmin"), not deleted, not blocked, and not the shared IRES admin login.
 *
 * Decision was made on 2026-09-30 (Taimoor): only such a person grants the admin-portal rights and
 * changes roles on the portal client, so every grant is traceable to a person. This mirrors IRES
 * Cloud's `isNamedPortalSysAdmin` (`PortalSysAdminGuard.ts`), which enforces the rule in
 * `saveRoleSettings` and `createOrUpdateUser`; the Office checks below only keep the screens in
 * step with it. Deliberately not a capability: on the IRES-typed portal client every capability in
 * `IRES_CAPABILITIES` is held by every user, which is the hole this closes.
 */
export function isNamedPortalSysAdmin(activeUser: User | null) {
  return (
    isOfficeAdminPortalClient() &&
    !!activeUser &&
    activeUser.role === Role.ADMIN &&
    !isIresAdminAccount(activeUser, true) &&
    activeUser.deleted !== true &&
    activeUser.blocked !== true
  );
}

/**
 * Whether the user may change a user's role, or create a user, as far as the admin portal client's
 * rule is concerned: anywhere but the portal client, yes (the usual capabilities still apply); on
 * the portal client, only a named SysAdmin, because a role decides who holds the portal's rights.
 * IRES Cloud refuses the save otherwise; this keeps the form from offering it.
 */
export function portalRoleChangeAccess(activeUser: User | null) {
  return !isOfficeAdminPortalClient() || isNamedPortalSysAdmin(activeUser);
}

export function createUserAccess(activeUser: User | null) {
  return (
    manageUsersClientAccess() &&
    hasCapability(
      activeUser, 
      Capability.CREATE_USER,
      
      UserCapability.EDIT_ADMIN,
      UserCapability.EDIT_USER
    ) &&
    // On the admin portal client only a named SysAdmin creates users; Cloud enforces it.
    portalRoleChangeAccess(activeUser)
  );
}

export function deleteUserAccess(activeUser: User | null) {
  return (
    manageUsersClientAccess() &&
    hasCapability(
      activeUser, 
      Capability.DELETE_USER,
      
      UserCapability.EDIT_ADMIN,
      UserCapability.EDIT_USER
    )
  );
}

export function editAdminAccess(activeUser: User | null) {
  return (
    manageUsersClientAccess() &&
    hasCapability(
      activeUser, 
      Capability.EDIT_ADMIN,
      
      UserCapability.EDIT_ADMIN
    )
  );
}

/**
 * Whether a user may switch the admin flag on their **own** account.
 *
 * The flag is otherwise self-locked: `editAdminAccess` alone lets an admin grant or revoke admin
 * rights for everybody but themselves, which stops both self-escalation and self-lockout.
 * IRES support needs the exception to be able to drop its own admin rights and see the portal the
 * way a client user sees it.
 *
 * Deliberately narrower than every other IRES capability in this file: users of an IRES-type
 * client are excluded, only the shared `IRES_ADMIN_ACCOUNT_ID` login qualifies. Both checks honour
 * "Spezialrechte für IRES-Kunden / IRES-Admin": turning it off removes the exception and restores
 * the self-lock.
 *
 * This is a *modifier* on `editAdminAccess`, not a standalone gate: it carries no client-layer
 * check because every call site combines it with `editAdminAccess`, which enforces
 * `manageUsersClientAccess()`.
 */
export function editOwnAdminAccess(activeUser: User | null) {
  return (
    isIresAdminAccount(activeUser) &&
    hasCapability(
      activeUser,
      Capability.EDIT_OWN_ADMIN
    )
  );
}

/**
 * Whether `activeUser` may switch the admin flag on `targetUser`'s record.
 *
 * `editAdminAccess` covers the capability; this adds the self-lock and its exception, and is the
 * gate the user form has to ask. `targetUser` is `null` while a user is being created, which is
 * never the active user's own record.
 *
 * The records are compared by id, not by object identity: the active user reaches the user form as
 * the store object from `getReactiveActiveUser()` today, but that helper falls back to the
 * localStorage copy, and an identity check would silently unlock the field for that copy.
 */
export function editAdminFlagAccess(
  activeUser: User | null,
  targetUser: User | null
) {
  const isOwnRecord = targetUser?.id === activeUser?.id;

  return (
    (!isOwnRecord || editOwnAdminAccess(activeUser)) &&
    editAdminAccess(activeUser)
  );
}

export function editUsersAccess(activeUser: User | null) {
  return (
    manageUsersClientAccess() &&
    hasCapability(
      activeUser, 
      Capability.EDIT_USER,
      
      UserCapability.EDIT_USER
    )
  );
}

export function editValidationAccess(activeUser: User | null) {
  return (
    manageUsersClientAccess() &&
    hasCapability(
      activeUser, 
      Capability.EDIT_VALIDATION,
      
      UserCapability.EDIT_VALIDATION
    )
  );
}

export function basicLicenceAccess(activeUser: User | null) {
  return (
    manageUsersClientAccess() &&
    hasCapability(
      activeUser, 
      Capability.BASIC_LICENCE,
      
      UserCapability.BASIC_LICENCE
    )
  );
}

export function editSharedEmailAddressAccess(activeUser: User | null) {
  return (
    manageUsersClientAccess() &&
    hasCapability(
      activeUser,
      Capability.EDIT_SHARED_EMAIL_ADDRESS
    )
  );
}

export function skipSettingSessionTokenAccess(activeUser: User | null) {
  return hasCapability(
    activeUser,
    Capability.SKIP_SETTING_SESSION_TOKEN
  );
}

export function grantAllLicencesAccess(activeUser: User | null) {
  return hasCapability(
    activeUser,
    Capability.GRANT_ALL_LICENCES
  );
}

export function defineSharedUserAccess(activeUser: User | null) {
  return hasCapability(
    activeUser,
    Capability.DEFINE_SHARED_USER_ACCESS,

    UserCapability.EDIT_ADMIN
  );
}

export function defineSharedUserMasterClientAccess(activeUser: User | null) {
  return hasCapability(
    activeUser,
    Capability.DEFINE_SHARED_USER_MASTER_CLIENT_ACCESS
  );
}

export function skipUserDataValidationAccess(activeUser: User | null) {
  return hasCapability(
    activeUser,
    Capability.SKIP_USER_DATA_VALIDATION
  );
}
/* * * * * * * * * *  * * * * * * * * */

/* * * * * * * * * *  * * * * * * * * */
/* * * * * * ADMINISTRATION * * * * * */
/* * * * * * * * * *  * * * * * * * * */
export function viewAdminsitrationSettingsAccess(activeUser: User | null) {
  return (
    configureAdminsitrationSettingsClientAccess() &&
    hasCapability(
      activeUser,
      Capability.CONFIGURE_ADMINISTRATION_SETTINGS,

      UserCapability.SETTINGS_ADMINISTRATION,
    )
  );
}

export function viewSystemConfigurationDistributionAccess(activeUser: User | null) {
  return (
    configureAdminsitrationSettingsClientAccess() &&
    hasCapability(
      activeUser,
      Capability.SYSTEM_CONFIGURATION_DISTRIBUTION,

      UserCapability.SETTINGS_ADMINISTRATION,
    )
  );
}

export function viewHyDryDevicesOfMemberAccess(activeUser: User | null) {
  return (
    deviceAdministrationAccess() &&
    hasCapability(
      activeUser,
      Capability.VIEW_HY_DRY_DEVICES_OF_MEMBER_CLIENTS,

      UserCapability.DEVICE_ADMINISTRATION,
    )
  );
}

export function viewClientSelectorAccess(activeUser: User | null) {
  return (
    deviceAdministrationAccess() &&
    hasCapability(
      activeUser,
      Capability.VIEW_CLIENT_SELECTOR,

      UserCapability.DEVICE_ADMINISTRATION,
    )
  );
}

export function viewDryingHistoryOfMemberAccess(activeUser: User | null) {
  return (
    deviceAdministrationAccess() &&
    hasCapability(
      activeUser,
      Capability.VIEW_DRYING_HISTORY_OF_MEMBER_CLIENTS,

      UserCapability.DEVICE_ADMINISTRATION,
    )
  );
}

export function viewHyDryDeviceDetailsOfMemberAccess(activeUser: User | null) {
  return (
    deviceAdministrationAccess() &&
    hasCapability(
      activeUser,
      Capability.VIEW_HY_DRY_DEVICE_DETAILS_OF_MEMBER_CLIENTS,

      UserCapability.DEVICE_ADMINISTRATION,
    )
  );
}

export function moveDevicesToClientUserAccess(activeUser: User | null) {
  return (
    deviceAdministrationAccess() &&
    hasCapability(
      activeUser,
      Capability.MOVE_DEVICES_TO_CLIENT,

      UserCapability.DEVICE_ADMINISTRATION,
    )
  );
}

export function defineOwnSystemConfigurationAccess(activeUser: User | null) {
  return (
    defineOwnSystemConfigurationClientAccess() &&
    hasCapability(
      activeUser,
      Capability.DEFINE_SYSTEM_CONFIGURATION
    )
  );
}

export function viewExternalSystemsAccess(activeUser: User | null) {
  return (
    viewExternalSystemsClientAccess() &&
    hasCapability(
      activeUser,
      Capability.VIEW_EXTERNAL_SYSTEMS
    )
  );
}

export function createMemberClientAccess(activeUser: User | null) {
  return (
    clientAdministrationAccess() &&
    hasCapability(
      activeUser,
      Capability.CREATE_MEMBER_CLIENTS,

      UserCapability.CLIENT_ADMINISTRATION
    )
  );
}

export function manageMemberClientAccess(activeUser: User | null) {
  return (
    clientAdministrationAccess() &&
    hasCapability(
      activeUser,
      Capability.MANAGE_MEMBER_CLIENTS,

      UserCapability.CLIENT_ADMINISTRATION
    )
  );
}

export function configureAdministrationCapabilitiesAccess(activeUser: User | null) {
  return (
    configureAdministrationCapabilitiesClientAccess() &&
    hasCapability(
      activeUser,
      Capability.CONFIGURE_ADMINISTRATION_CAPABILITIES
    )
  );
}

/* * * * * * * * * *  * * * * * * * * */
/* * * * * *  ADMIN PORTAL  * * * * * */
/* * * * * * * * * *  * * * * * * * * */

/**
 * Whether the user may rate the quality of AI drying predictions in the admin portal.
 *
 * This is the "Drying Expert" permission, and it resolves **only** through role settings:
 * `Capability.RATE_DRYINGS` is deliberately absent from `IRES_CAPABILITIES`, `ADMIN_CAPABILITIES`
 * and `GENERAL_CAPABILITIES`, so `hasCapability()` falls through to `doesUserHaveCapability()`.
 * Without that, every user of the IRES-typed admin client would hold it automatically and a
 * reviewer-only role would be impossible.
 *
 * Unlike most access functions this checks the **user layer only**. The matching client layer is
 * `isOfficeAdminClient()`, which reads Firestore and is therefore async; it is applied in
 * `router.beforeEach` via `ADMIN_PORTAL_COMPONENTS` (see `src/utils/RouteGuards.ts`). That split is
 * intentional, not an omission.
 */
export function rateDryingsAccess(activeUser: User | null) {
  return hasCapability(
    activeUser,
    Capability.RATE_DRYINGS,

    UserCapability.RATE_DRYINGS
  );
}

/**
 * Whether the user may work with production data in the admin portal: the device test reports the
 * production rig uploads, and the device creation flows as they migrate out of the IRES Admin
 * Android app.
 *
 * Resolves **only** through role settings, for the same reason as {@link rateDryingsAccess}:
 * `Capability.MANAGE_PRODUCTION` is deliberately absent from `IRES_CAPABILITIES`,
 * `ADMIN_CAPABILITIES` and `GENERAL_CAPABILITIES`, so `hasCapability()` falls through to
 * `doesUserHaveCapability()`. Without that every user of the IRES-typed admin client would hold it
 * and a production-only role would be impossible.
 *
 * Also the user layer only. The matching client layer is `isOfficeAdminClient()`, which reads
 * Firestore and is therefore async; it is applied in `router.beforeEach` via
 * `ADMIN_PORTAL_COMPONENTS` (see `src/utils/RouteGuards.ts`).
 */
export function manageProductionAccess(activeUser: User | null) {
  return hasCapability(
    activeUser,
    Capability.MANAGE_PRODUCTION,

    UserCapability.MANAGE_PRODUCTION
  );
}

/**
 * Whether the user may administer clients in the admin portal: the clients list, and the client
 * detail, properties and creation screens as they migrate out of the IRES Admin Android app.
 *
 * Resolves **only** through role settings, like {@link manageProductionAccess}:
 * `Capability.MANAGE_CLIENTS` is absent from all three capability groups, so being a user of the
 * IRES-typed admin client is not enough on its own.
 *
 * User layer only; the client layer (`isOfficeAdminClient()`) is async and applied in
 * `router.beforeEach` via `ADMIN_PORTAL_COMPONENTS` (see `src/utils/RouteGuards.ts`).
 */
export function manageClientsAccess(activeUser: User | null) {
  return hasCapability(
    activeUser,
    Capability.MANAGE_CLIENTS,

    UserCapability.MANAGE_CLIENTS
  );
}

/**
 * Whether the user may change anything in the IRES Admin Client (the Mandanten, PDF-Vorlagen, Mira lists
 * and RENO ratings, each behind its own access check) and open a client with "Als Mandant anmelden"
 * (#2610069): every user but the shared IRES admin login, which is read-only there (Taimoor, 2026-10-19,
 * #2610071), because every change must name a person.
 *
 * Mirrors IRES Cloud, which refuses the shared login (`SHARED_LOGIN`) in every portal write callable
 * (`assertMayChangeAsAdministrator`, `isAttributableCaller`) and in the RENO rules
 * (`mayWriteAsReviewer`); this keeps the screens from offering what would be refused.
 */
export function adminPortalChangeAccess(activeUser: User | null) {
  return !!activeUser && !isIresAdminAccount(activeUser, true);
}

/**
 * Whether the user may see and configure the admin-portal capabilities in the role settings editor:
 * a named SysAdmin of the portal client only ({@link isNamedPortalSysAdmin}).
 *
 * The client layer is part of that: without it the rows appeared for every `clientType: "IRES"`
 * client (around 30 of them, e.g. "IRES Standard"), none of which can reach the portal the rights
 * apply to.
 */
export function configureAdminPortalCapabilitiesAccess(activeUser: User | null) {
  return isNamedPortalSysAdmin(activeUser);
}


/* * * * * * * * * *  * * * * * * * * */
/* * * *  DATA VIEWER / LV  * * * * * */
/* * * * * * * * * *  * * * * * * * * */

/** Whether the user can edit data in the data viewers (PDE, LV, Beschreibung). */
export function editDataViewerAccess(activeUser: User | null) {
  return hasCapability(activeUser, Capability.EDIT_DATA_VIEWER);
}

/**
 * Whether the user can view and manage billing catalogs (Leistungslisten) in Settings.
 * Restricted to IRES admin accounts with the SystemConfiguration toggle enabled.
 */
export function billingCatalogSettingsAccess(activeUser: User | null) {
  return (
    isIresAdminAccount(activeUser) &&
    getBillingCatalogsAccess() &&
    hasCapability(activeUser, Capability.VIEW_BILLING_CATALOGS)
  );
}
/* * * * * * * * * *  * * * * * * * * */